import React, { useState, useEffect, useCallback } from 'react';
import collegeConfig from '../../config/collegeConfig';
import { saveFile } from '../../utils/saveFile';
import {
  FaHospital, FaUserMd, FaCalendarAlt, FaCheckCircle, FaTimesCircle,
  FaAward, FaBook, FaClipboardList, FaHeartbeat, FaTimes, FaHistory,
  FaUpload, FaDownload, FaChevronDown, FaChevronUp, FaExclamationTriangle,
  FaSpinner
} from 'react-icons/fa';

// Read-only Classic DRP detail view (current posting + full history), shared
// by the HOD's ClassicDRPMonitor and the monitor/dean/department_monitor
// Students tab. Self-contained: fetches `/classic-drp/hod/student/:studentId`
// on mount, which is scoped server-side per the caller's role.

const API = collegeConfig.api.baseURL;

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isCompleted = (toDate) => {
  if (!toDate) return false;
  const t = new Date(toDate); t.setHours(0, 0, 0, 0);
  const n = new Date(); n.setHours(0, 0, 0, 0);
  return t < n;
};

const logCount = (entry) =>
  (entry?.attendance?.length || 0) + (entry?.diary?.length || 0) +
  (entry?.operations?.length || 0) + (entry?.publicHealth?.length || 0);

const isCertified = (entry) => !!(entry?.certificate?.issued || entry?.uploadedCertificate?.fileName);

const RecordRow = ({ label, value, sub }) => (
  <div style={{ display: 'flex', gap: 10, padding: '5px 0', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
    <span style={{ fontSize: 12, color: '#888', minWidth: 90 }}>{label}</span>
    <span style={{ fontSize: 13, color: '#333', flex: 1 }}>
      {value}
      {sub && <span style={{ fontSize: 11, color: '#777', display: 'block' }}>{sub}</span>}
    </span>
  </div>
);

const LogSection = ({ icon, title, items, renderItem }) => {
  const count = items?.length || 0;
  if (count === 0) {
    return (
      <div style={styles.emptyLogLine}>
        {icon}
        <span>{title} — not recorded</span>
      </div>
    );
  }
  return (
    <div style={styles.sectionBlock}>
      <h3 style={styles.sectionTitle}>{icon}{title} ({count})</h3>
      {items.map(renderItem)}
    </div>
  );
};

const HistoryDRPCard = ({ hist, index, studentId, token, onDownloadSystemCert }) => {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const v = hist.inchargeVerification || {};
  const hasCert = hist.certificate?.issued || hist.uploadedCertificate?.fileName;

  const downloadUploadedCert = async () => {
    setDownloading(true);
    try {
      const r = await fetch(`${API}/classic-drp/hod/student/${studentId}/history/${hist._id}/certificate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) {
        await saveFile(d.data, d.mimeType, d.fileName || 'certificate');
      }
    } catch { /* silent */ }
    setDownloading(false);
  };

  return (
    <div style={{ border: hasCert ? '1px solid #e5e7eb' : '1px solid #fca5a5', borderRadius: 10, padding: 12, marginBottom: 8, background: hasCert ? '#fafafa' : '#fff9f9' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ fontSize: 11, padding: '3px 7px', borderRadius: 8, background: hasCert ? '#d1fae5' : '#fee2e2', color: hasCert ? '#065f46' : '#b91c1c', fontWeight: 700, flexShrink: 0 }}>
          DRP {index}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 6 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{hist.profile?.hospitalName || '—'}</div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                <FaCalendarAlt size={10} style={{ marginRight: 4 }} />
                {formatDate(hist.profile?.fromDate)} – {formatDate(hist.profile?.toDate)}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                <FaUserMd size={10} style={{ marginRight: 4 }} />
                {hist.profile?.inchargeName || (hist.profile?.inchargeId ? 'Incharge assigned' : <em>No Incharge</em>)}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
              {hist.certificate?.issued && <span style={styles.badgeSystemCert}><FaAward size={9} style={{ marginRight: 3 }} />System Cert</span>}
              {hist.uploadedCertificate?.fileName && <span style={styles.badgeUploadedCert}><FaUpload size={9} style={{ marginRight: 3 }} />Uploaded Cert</span>}
              {!hasCert && <span style={styles.badgeNoCert}><FaExclamationTriangle size={9} style={{ marginRight: 3 }} />No Certificate</span>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
            {[
              { key: 'attendanceVerified', label: 'Att' },
              { key: 'diaryVerified', label: 'Diary' },
              { key: 'operationsVerified', label: 'Ops' },
              { key: 'publicHealthVerified', label: 'PH' }
            ].map(item => (
              <span key={item.key} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, background: v[item.key] ? '#d1fae5' : '#f3f4f6', color: v[item.key] ? '#065f46' : '#999' }}>
                {v[item.key] ? '✓' : '○'} {item.label}
              </span>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {hist.certificate?.issued && (
              <button onClick={() => onDownloadSystemCert(hist)} style={styles.miniBtn}>
                <FaDownload size={10} style={{ marginRight: 4 }} /> System Certificate
              </button>
            )}
            {hist.uploadedCertificate?.fileName && (
              <button onClick={downloadUploadedCert} disabled={downloading} style={{ ...styles.miniBtn, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                {downloading ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 4 }} /> : <FaDownload size={10} style={{ marginRight: 4 }} />}
                Uploaded Certificate
              </button>
            )}
            <button onClick={() => setExpanded(x => !x)} style={{ ...styles.miniBtn, background: '#f3f4f6', color: '#555', border: '1px solid #e5e7eb' }}>
              {expanded ? <FaChevronUp size={10} style={{ marginRight: 4 }} /> : <FaChevronDown size={10} style={{ marginRight: 4 }} />}
              {expanded ? 'Hide' : 'Details'}
            </button>
          </div>

          {expanded && (
            <div style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12, marginBottom: 8 }}>
                <div><span style={{ color: '#888' }}>Attendance: </span><strong>{hist.attendance?.length || 0} days</strong></div>
                <div><span style={{ color: '#888' }}>Diary: </span><strong>{hist.diary?.length || 0} entries</strong></div>
                <div><span style={{ color: '#888' }}>Operations: </span><strong>{hist.operations?.length || 0}</strong></div>
                <div><span style={{ color: '#888' }}>Public Health: </span><strong>{hist.publicHealth?.length || 0}</strong></div>
              </div>
              {v.remarks && (
                <div style={{ padding: '8px 10px', background: '#f8f8f8', borderRadius: 8, borderLeft: '3px solid #0f766e', fontSize: 12, marginBottom: 8 }}>
                  <strong>Incharge Remarks:</strong> {v.remarks}
                </div>
              )}
              {hist.certificate?.issuedAt && <div style={{ fontSize: 12, color: '#059669', marginBottom: 4 }}>System cert issued: {formatDate(hist.certificate.issuedAt)}</div>}
              {hist.uploadedCertificate?.uploadedAt && (
                <div style={{ fontSize: 12, color: '#2563eb' }}>
                  Uploaded: {formatDate(hist.uploadedCertificate.uploadedAt)}
                  {hist.uploadedCertificate.fileName && <span style={{ color: '#666' }}> ({hist.uploadedCertificate.fileName})</span>}
                </div>
              )}

              {hist.diary?.length > 0 && (
                <details style={{ marginTop: 10 }}>
                  <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#2563eb' }}>Daily Diary ({hist.diary.length})</summary>
                  <div style={{ marginTop: 6 }}>
                    {hist.diary.map((d, i) => (
                      <RecordRow key={i} label={`${formatDate(d.date)}${d.day ? ` (${d.day})` : ''}`} value={d.entry} />
                    ))}
                  </div>
                </details>
              )}
              {hist.operations?.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#7c3aed' }}>Operations ({hist.operations.length})</summary>
                  <div style={{ marginTop: 6 }}>
                    {hist.operations.map((op, i) => (
                      <RecordRow key={i} label={formatDate(op.date)} value={`${op.patientName}${op.ipNumber ? ` · IP: ${op.ipNumber}` : ''}`} sub={op.category} />
                    ))}
                  </div>
                </details>
              )}
              {hist.publicHealth?.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#dc6803' }}>Public Health ({hist.publicHealth.length})</summary>
                  <div style={{ marginTop: 6 }}>
                    {hist.publicHealth.map((ph, i) => (
                      <RecordRow key={i} label={formatDate(ph.date)} value={ph.activity} />
                    ))}
                  </div>
                </details>
              )}
              {hist.attendance?.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#16a085' }}>Attendance ({hist.attendance.length})</summary>
                  <div style={{ marginTop: 6 }}>
                    {hist.attendance.map((a, i) => (
                      <RecordRow key={i} label={formatDate(a.date)} value={a.posting} />
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main modal ─────────────────────────────────────────────────────────────
const DrpHistoryModal = ({ studentId, onClose }) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certDownloading, setCertDownloading] = useState(null);
  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/classic-drp/hod/student/${studentId}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setEntry(d.entry);
    } catch { /* silent */ }
    setLoading(false);
  }, [studentId, token]);

  useEffect(() => { load(); }, [load]);

  const downloadSystemCert = async (profileData, studentInfo) => {
    try {
      const { generateClassicDRPCertificate } = await import('../../utils/classicDrpCertificateGenerator');
      const studentName = `${studentInfo?.profile?.firstName || ''} ${studentInfo?.profile?.lastName || ''}`.trim();
      const courseName = studentInfo?.assignedCourses?.[0]?.courseName || '';
      const inchargeData = profileData?.inchargeId;
      const inchargeName = profileData?.inchargeName ||
        (inchargeData ? `${inchargeData.profile?.firstName || ''} ${inchargeData.profile?.lastName || ''}`.trim() : '');
      await generateClassicDRPCertificate(
        { studentName, courseName, hospitalName: profileData?.hospitalName, fromDate: profileData?.fromDate, toDate: profileData?.toDate, inchargeName },
        collegeConfig.collegeName, collegeConfig.logo
      );
    } catch { /* silent */ }
  };

  const downloadUploadedCert = async (historyId) => {
    const path = historyId
      ? `/hod/student/${studentId}/history/${historyId}/certificate`
      : `/hod/student/${studentId}/certificate`;
    const key = historyId || 'current';
    setCertDownloading(key);
    try {
      const r = await fetch(`${API}/classic-drp${path}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) {
        await saveFile(d.data, d.mimeType, d.fileName || 'certificate');
      }
    } catch { /* silent */ }
    setCertDownloading(null);
  };

  if (loading) {
    return (
      <div style={styles.modal}>
        <div style={{ ...styles.modalContent, ...styles.centered }}>
          <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 24, color: '#b11217' }} />
          <style>{`@keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>
        </div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div style={styles.modal} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={styles.modalContent}>
          <div style={styles.modalHeader}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>DRP History</h2>
            <button onClick={onClose} style={styles.closeBtn}><FaTimes /></button>
          </div>
          <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>No DRP data recorded yet.</div>
        </div>
      </div>
    );
  }

  const s = entry.student;
  const v = entry.inchargeVerification || {};
  const inchargeData = entry.profile?.inchargeId;
  const inchargeName = inchargeData ? `${inchargeData.profile?.firstName || ''} ${inchargeData.profile?.lastName || ''}`.trim() : '—';
  const studentName = `${s?.profile?.firstName || ''} ${s?.profile?.lastName || ''}`.trim();
  const drpHistory = entry.drpHistory || [];
  const totalDRPs = drpHistory.length + (entry.profile?.hospitalName ? 1 : 0);
  const currentCompleted = isCompleted(entry.profile?.toDate);
  const certified = isCertified(entry);
  const hasAnyLogs = logCount(entry) > 0;

  return (
    <div style={styles.modal} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{studentName}</h2>
            <span style={{ fontSize: 12, color: '#888' }}>
              {s?.assignedCourses?.[0]?.courseName || ''} · {s?.profile?.registrationNumber || ''}
              {totalDRPs > 1 && <span style={{ marginLeft: 8, background: '#ede9fe', color: '#7c3aed', padding: '1px 7px', borderRadius: 10, fontWeight: 600 }}>{totalDRPs} DRPs</span>}
            </span>
          </div>
          <button onClick={onClose} style={styles.closeBtn}><FaTimes /></button>
        </div>

        <div style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 70px)', padding: 16 }}>

          {entry.profile?.hospitalName && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <FaHospital style={{ color: '#b11217' }} />
                <span style={{ fontWeight: 700, fontSize: 15 }}>Current DRP</span>
                {currentCompleted && (
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: '#d1fae5', color: '#065f46', fontWeight: 600 }}>Completed</span>
                )}
              </div>

              <div style={styles.infoCard}>
                <div style={styles.detailGrid}>
                  <div><span style={styles.dLabel}>Hospital</span><span style={styles.dVal}>{entry.profile?.hospitalName || '—'}</span></div>
                  <div><span style={styles.dLabel}>Duration</span><span style={styles.dVal}>{formatDate(entry.profile?.fromDate)} – {formatDate(entry.profile?.toDate)}</span></div>
                  <div><span style={styles.dLabel}>Incharge</span><span style={styles.dVal}>{inchargeName}</span></div>
                  <div>
                    <span style={styles.dLabel}>Certificate</span>
                    <span style={{ ...styles.dVal }}>
                      {entry.certificate?.issued ? (
                        <span style={{ color: '#059669' }}>System — Issued {formatDate(entry.certificate.issuedAt)}</span>
                      ) : entry.uploadedCertificate?.fileName ? (
                        <span style={{ color: '#2563eb' }}>Uploaded — {formatDate(entry.uploadedCertificate.uploadedAt)}</span>
                      ) : (
                        <span style={{ color: '#888' }}>Not issued</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {entry.certificate?.issued && (
                  <button onClick={() => downloadSystemCert(entry.profile, s)} style={styles.miniBtn}>
                    <FaDownload size={10} style={{ marginRight: 4 }} /> System Certificate
                  </button>
                )}
                {entry.uploadedCertificate?.fileName && (
                  <button onClick={() => downloadUploadedCert(null)} disabled={certDownloading === 'current'} style={{ ...styles.miniBtn, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                    {certDownloading === 'current' ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 4 }} /> : <FaDownload size={10} style={{ marginRight: 4 }} />}
                    Uploaded Certificate ({entry.uploadedCertificate.fileName})
                  </button>
                )}
              </div>

              {/* Verification status — only meaningful once there's something to verify */}
              {hasAnyLogs ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                    {[
                      { key: 'attendanceVerified', label: 'Attendance' },
                      { key: 'diaryVerified', label: 'Diary' },
                      { key: 'operationsVerified', label: 'Operations' },
                      { key: 'publicHealthVerified', label: 'Public Health' }
                    ].map(item => (
                      <div key={item.key} style={{ ...styles.badge, background: v[item.key] ? '#d1fae5' : '#fee2e2' }}>
                        {v[item.key] ? <FaCheckCircle style={{ color: '#059669', marginRight: 6 }} /> : <FaTimesCircle style={{ color: '#dc2626', marginRight: 6 }} />}
                        <span style={{ fontSize: 12 }}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                  {v.remarks && (
                    <div style={{ marginBottom: 14, padding: 10, background: '#f8f8f8', borderRadius: 8, borderLeft: '3px solid #0f766e', fontSize: 13 }}>
                      <strong>Incharge Remarks:</strong> {v.remarks}
                    </div>
                  )}
                </>
              ) : (
                <div style={certified ? styles.infoBannerGreen : styles.infoBannerNeutral}>
                  <FaCheckCircle style={{ color: certified ? '#059669' : '#888', marginRight: 8, flexShrink: 0, marginTop: 2 }} />
                  <span>
                    {certified
                      ? `Posting completed via ${entry.certificate?.issued ? 'incharge-issued' : 'student-uploaded'} certificate. No attendance, diary, operations, or public health logs were recorded — this is the standard workflow for most students and does not indicate missing data.`
                      : 'No activity logs or certificate recorded for this posting yet.'}
                  </span>
                </div>
              )}

              <LogSection icon={<FaCalendarAlt style={{ marginRight: 8, color: '#16a085' }} />} title="Attendance" items={entry.attendance} renderItem={a => <RecordRow key={a._id} label={formatDate(a.date)} value={a.posting} />} />
              <LogSection icon={<FaBook style={{ marginRight: 8, color: '#2563eb' }} />} title="Daily Diary" items={entry.diary} renderItem={d => <RecordRow key={d._id} label={`${formatDate(d.date)} (${d.day || ''})`} value={d.entry} />} />
              <LogSection icon={<FaClipboardList style={{ marginRight: 8, color: '#7c3aed' }} />} title="Operations" items={entry.operations} renderItem={op => <RecordRow key={op._id} label={formatDate(op.date)} value={`${op.patientName}${op.ipNumber ? ` · IP: ${op.ipNumber}` : ''}`} sub={op.category} />} />
              <LogSection icon={<FaHeartbeat style={{ marginRight: 8, color: '#dc6803' }} />} title="Public Health" items={entry.publicHealth} renderItem={ph => <RecordRow key={ph._id} label={formatDate(ph.date)} value={ph.activity} />} />
            </>
          )}

          {drpHistory.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingTop: entry.profile?.hospitalName ? 8 : 0, borderTop: entry.profile?.hospitalName ? '1px solid #e5e7eb' : 'none' }}>
                <FaHistory style={{ color: '#6366f1' }} />
                <span style={{ fontWeight: 700, fontSize: 15 }}>Past DRP History</span>
                <span style={{ marginLeft: 'auto', background: '#ede9fe', color: '#7c3aed', fontSize: 12, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                  {drpHistory.length} past DRP{drpHistory.length > 1 ? 's' : ''}
                </span>
              </div>
              {[...drpHistory].reverse().map((hist, idx) => (
                <HistoryDRPCard
                  key={hist._id}
                  hist={hist}
                  index={drpHistory.length - idx}
                  studentId={s?._id}
                  token={token}
                  onDownloadSystemCert={(h) => downloadSystemCert(h.profile || h, s)}
                />
              ))}
            </div>
          )}

          {!entry.profile?.hospitalName && drpHistory.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>
              <FaHospital style={{ fontSize: 32, color: '#ddd', marginBottom: 10 }} />
              <p>No DRP data recorded yet.</p>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>
    </div>
  );
};

const styles = {
  centered: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40, gap: 10 },
  miniBtn: { fontSize: 12, padding: '5px 10px', borderRadius: 8, background: '#fff7ed', color: '#b45309', border: '1px solid #fed7aa', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontWeight: 600 },
  badgeSystemCert: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#d97706', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeUploadedCert: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeNoCert: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#fee2e2', color: '#dc2626', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 },
  modalContent: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid #e5e7eb' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#666', padding: 4 },
  infoCard: { background: '#f9fafb', borderRadius: 10, padding: 12, marginBottom: 14 },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  dLabel: { display: 'block', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 },
  dVal: { display: 'block', fontSize: 13, color: '#1a1a2e', fontWeight: 500 },
  badge: { display: 'flex', alignItems: 'center', padding: '7px 10px', borderRadius: 8 },
  sectionBlock: { marginBottom: 14, background: '#f9fafb', borderRadius: 10, padding: 12 },
  sectionTitle: { margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#1a1a2e', display: 'flex', alignItems: 'center' },
  empty: { color: '#999', fontSize: 13, fontStyle: 'italic', margin: '4px 0' },
  emptyLogLine: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 4px', fontSize: 12, color: '#999' },
  infoBannerGreen: { display: 'flex', alignItems: 'flex-start', gap: 4, padding: 12, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, fontSize: 13, color: '#065f46', marginBottom: 14, lineHeight: 1.5 },
  infoBannerNeutral: { display: 'flex', alignItems: 'flex-start', gap: 4, padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13, color: '#555', marginBottom: 14, lineHeight: 1.5 }
};

export default DrpHistoryModal;
